import { useMemo } from 'react';
import {
  prefixStorageKey,
  getLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import {
  TABLE_COLUMN_WIDTH_STORAGE_KEY,
  TABLE_COLUMN_VISIBILITY_STORAGE_KEY,
  TABLE_COLUMN_PINNED_STORAGE_KEY,
  TABLE_COLUMN_ORDER_STORAGE_KEY,
  TABLE_MIN_COLUMN_WIDTH,
  TABLE_COLUMN_HEADER_ICON_WIDTH,
} from '@antimatterhq/ui/constants/table';

// TODO: Persist column states in local storage
export function useInitialTableColumnState(
  columnDefinitions: TableColumnDefinition[],
  localStorageKey: string,
  persistState: boolean = true,
) {
  const initialColumnPinned = useMemo(() => {
    let storedState: Record<string, 'left' | 'right' | undefined> | undefined =
      undefined;

    if (persistState) {
      storedState = getLocalStorageValue<
        Record<string, 'left' | 'right' | undefined>
      >(prefixStorageKey(localStorageKey, TABLE_COLUMN_PINNED_STORAGE_KEY));
    }

    if (
      persistState &&
      storedState &&
      Object.keys(storedState).length === columnDefinitions.length
    ) {
      return storedState;
    }

    return columnDefinitions.reduce(
      (acc, col) => ({ ...acc, [col.id]: col.pinned }),
      {} as Record<string, 'left' | 'right' | undefined>,
    );
  }, [columnDefinitions, localStorageKey, persistState]);

  const initialColumnOrders = useMemo(() => {
    let storedState: Record<string, number> | undefined = undefined;

    if (persistState) {
      storedState = getLocalStorageValue<Record<string, number>>(
        prefixStorageKey(localStorageKey, TABLE_COLUMN_ORDER_STORAGE_KEY),
      );
    }

    if (
      persistState &&
      storedState &&
      Object.keys(storedState).length === columnDefinitions.length
    ) {
      return storedState;
    }

    return columnDefinitions
      .filter((col) => !col.pinned)
      .reduce(
        (acc, col, index) => ({ ...acc, [col.id]: col.order ?? index }),
        {} as Record<string, number>,
      );
  }, [columnDefinitions, localStorageKey, persistState]);

  const initialColumnVisibility = useMemo(() => {
    let storedState: Record<string, boolean> | undefined = undefined;

    if (persistState) {
      storedState = getLocalStorageValue<Record<string, boolean>>(
        prefixStorageKey(localStorageKey, TABLE_COLUMN_VISIBILITY_STORAGE_KEY),
      );
    }

    if (
      persistState &&
      storedState &&
      Object.keys(storedState).length === columnDefinitions.length
    ) {
      return storedState;
    }

    return columnDefinitions.reduce(
      (acc, col) => ({ ...acc, [col.id]: !col.hidden }),
      {} as Record<string, boolean>,
    );
  }, [columnDefinitions, localStorageKey, persistState]);

  const initialColumnWidths = useMemo(() => {
    let storedState: Record<string, number> | undefined = undefined;

    if (persistState) {
      storedState = getLocalStorageValue<Record<string, number>>(
        prefixStorageKey(localStorageKey, TABLE_COLUMN_WIDTH_STORAGE_KEY),
      );
    }

    if (
      persistState &&
      storedState &&
      Object.keys(storedState).length === columnDefinitions.length
    ) {
      return storedState;
    }
    return columnDefinitions.reduce(
      (acc, col) => ({
        ...acc,
        [col.id]: Math.max(
          col.width ?? TABLE_MIN_COLUMN_WIDTH,
          col.title.length * 10 + (TABLE_COLUMN_HEADER_ICON_WIDTH + 4) * 2,
        ),
      }),
      {} as Record<string, number>,
    );
  }, [columnDefinitions, localStorageKey, persistState]);

  return {
    initialColumnPinned,
    initialColumnOrders,
    initialColumnVisibility,
    initialColumnWidths,
  };
}
